import { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { hapticLight } from '../../utils/haptics.js';
import './ViewEditModeToggle.css';

export type ViewEditMode = 'view' | 'edit';

interface ViewEditModeToggleProps {
  mode: ViewEditMode;
  onModeChange: (mode: ViewEditMode) => void;
  disabled?: boolean;
}

export interface ViewEditModeToggleRef {
  highlightViewOnly: () => void;
}

const ViewEditModeToggle = forwardRef<ViewEditModeToggleRef, ViewEditModeToggleProps>(({ 
  mode,
  onModeChange,
  disabled = false
}, ref) => {
  const viewOnlyRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px) to trigger mode change
  const minSwipeDistance = 30;

  const highlightViewOnly = () => {
    if (viewOnlyRef.current && mode === 'view') {
      // Trigger haptic feedback to indicate view-only mode
      hapticLight();
      
      // Add highlight class
      viewOnlyRef.current.classList.add('highlight');
      
      // Remove highlight class after a short delay (using CSS transitions)
      setTimeout(() => {
        if (viewOnlyRef.current) {
          viewOnlyRef.current.classList.remove('highlight');
        }
      }, 200); // Short flash duration
    }
  };

  useImperativeHandle(ref, () => ({
    highlightViewOnly
  }));

  const handleModeChange = (newMode: ViewEditMode) => {
    if (!disabled) {
      // Trigger haptic feedback on mode change
      hapticLight();
      onModeChange(newMode);
    }
  };

  // Touch event handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset end position
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || disabled) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Right swipe: view -> edit (swipe toward edit button on right)
    if (isRightSwipe && mode === 'view') {
      handleModeChange('edit');
    }
    
    // Left swipe: edit -> view (swipe toward view button on left)
    if (isLeftSwipe && mode === 'edit') {
      handleModeChange('view');
    }

    // Reset touch positions
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <>
      {/* Map overlay for view mode */}
      {mode === 'view' && (
        <div className="map-view-overlay" aria-hidden="true" />
      )}
      
      {/* Safety zone wrapper for toggle control */}
      <div className="view-edit-mode-toggle-safety-zone">
        {/* Toggle control */}
        <div className="view-edit-mode-toggle-overlay">
          <div 
            className={`view-edit-mode-toggle-container ${disabled ? 'disabled' : ''}`}
            role="radiogroup"
            aria-label="View/Edit mode toggle"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="toggle-track">
              <button
                className={`toggle-option ${mode === 'view' ? 'active' : 'inactive'} view`}
                onClick={() => handleModeChange('view')}
                disabled={disabled}
                aria-pressed={mode === 'view'}
                aria-label="View mode"
              >
                <VisibilityIcon />
              </button>
              <button
                className={`toggle-option ${mode === 'edit' ? 'active' : 'inactive'} edit`}
                onClick={() => handleModeChange('edit')}
                disabled={disabled}
                aria-pressed={mode === 'edit'}
                aria-label="Edit mode"
              >
                <EditIcon />
              </button>
            </div>
          </div>
          
          {/* View Only indicator */}
          {mode === 'view' && (
            <div ref={viewOnlyRef} className="view-only-indicator">
              View Only
            </div>
          )}
        </div>
      </div>
    </>
  );
});

ViewEditModeToggle.displayName = 'ViewEditModeToggle';

export default ViewEditModeToggle;